// RecorderWindow — abre como pop-up isolado do app principal. Capta a
// gravação inteira aqui — se o aluno der refresh, perde tudo (beforeunload
// avisa). Quando termina, salva localmente E sobe pro server pra transcrever.
//
// Fases:
//   0. setup     → form pré-gravação (tipo, disciplina, idioma, foco, fontes
//                   de áudio, vídeo opt-in)
//   1. recording → captura ativa: VU meter, auto-leveler, mute por trilha,
//                   timer; bloqueio de refresh
//   2. finalize  → toca preview do áudio + faz upload pra transcrição
//                   (com progresso) + permite download local
//   3. done      → mostra resumo do que foi gerado, link pra abrir no app
//
// Auto-leveler (ver memo project_recorder_audio_lectures): por trilha,
// AnalyserNode mede RMS, GainNode ajusta ganho gradualmente mirando -18dBFS.
// Compressor corta picos. Quando RMS < threshold por 500ms = silêncio, gain
// abaixa pra não captar chiado.

import { useEffect, useRef, useState } from "preact/hooks";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Circle,
  Download,
  Loader2,
  Mic,
  Pause,
  Play,
  Square,
  Volume2,
  VolumeX,
  X
} from "lucide-react";
import { Checkbox } from "@app/ui/components/checkbox";
import {
  api,
  ApiError,
  type CreateLectureInput,
  type LectureFocus,
  type LectureKind
} from "./api";

type Phase = "setup" | "permission" | "recording" | "finalize" | "done" | "error";

interface TrackController {
  id: string;
  label: string;
  source: "mic" | "system";
  stream: MediaStream;
  analyser: AnalyserNode;
  gain: GainNode;
  muted: boolean;
  currentRms: number; // 0..1
  autoGainDb: number; // dB aplicado pelo leveler
}

/** "default" = "use o mic default do sistema, sem deviceId fixo". Outras
 *  strings = deviceId vindo de navigator.mediaDevices.enumerateDevices(). */
type MicId = string;

interface SetupData {
  title: string;
  kind: LectureKind;
  theme: string;
  level: string;
  language: string;
  summaryFocus: LectureFocus;
  expectedSpeakers: number;
  mainSpeakerName: string;
  userNotes: string;
  captureKind: "audio_only" | "screen_and_audio";
  /** IDs dos microfones selecionados (USB, BT, built-in). "default" = mic
   *  padrão do sistema. Vazio = sem mic. */
  selectedMicIds: MicId[];
  /** Filtros por fonte de áudio. Key = MicId ou "system". */
  noiseSuppression: Record<string, boolean>;
  useSystem: boolean;
  /** Caderno (AiStudy session) que vai receber transcript+summary como
   *  documentos depois que a transcrição terminar. Null = não vinculado. */
  linkedAiStudySessionId: string | null;
}

const DEFAULT_SETUP: SetupData = {
  title: "",
  kind: "aula",
  theme: "",
  level: "",
  language: "pt-BR",
  summaryFocus: "detailed_notes",
  expectedSpeakers: 1,
  mainSpeakerName: "",
  userNotes: "",
  captureKind: "audio_only",
  selectedMicIds: ["default"],
  noiseSuppression: { default: true, system: false },
  useSystem: false,
  linkedAiStudySessionId: null
};

const TARGET_RMS_DB = -18;
const SILENCE_THRESHOLD_DB = -50;
const SILENCE_MS = 500;
const MAX_GAIN_DB = 18;
const MIN_GAIN_DB = -12;
const GAIN_STEP_DB = 0.5;

function rmsToDb(rms: number): number {
  if (rms <= 0) return -Infinity;
  return 20 * Math.log10(rms);
}
function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/** Canonicaliza um título pra basename de arquivo (lowercase, ASCII, hifens). */
function slugifyTitle(raw: string): string {
  return (
    raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "gravacao"
  );
}

/** Auto-download imediato do blob assim que a gravação termina — backup
 *  local antes do upload pra transcrição. Se Chrome bloquear (sem user
 *  gesture), o usuário ainda pode clicar "Baixar áudio" manualmente. */
function autoDownloadBlob(blob: Blob, title: string, mimeType: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
  const ext = mimeType.includes("video") ? "webm" : "webm";
  a.download = `${slugifyTitle(title || "gravacao")}-${ts}.${ext}`;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Representação simplificada de um audio input device. */
interface MicDeviceInfo {
  deviceId: string;
  label: string;
}

/** Acha o label de um deviceId na lista enumerada. "default" → "Microfone padrão". */
function labelForMicId(id: string, devices: MicDeviceInfo[]): string {
  if (id === "default") return "Microfone padrão";
  const d = devices.find((x) => x.deviceId === id);
  return d?.label || "";
}

/** Enumera microfones disponíveis. Os labels só vêm completos depois que
 *  o usuário concede permissão de áudio uma vez — antes disso, label vem
 *  vazio e mostramos "Microfone N". */
async function enumerateMicrophones(): Promise<MicDeviceInfo[]> {
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all
      .filter((d) => d.kind === "audioinput")
      .map((d, i) => ({
        deviceId: d.deviceId,
        label: d.label || `Microfone ${i + 1}`
      }));
  } catch {
    return [];
  }
}

function pickMimeType(): { audio: string; video: string } {
  // Browsers diferem muito. Webm/opus é o mais consistente em Chrome/Edge/FF.
  const audio = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
    ? "audio/webm;codecs=opus"
    : MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : "audio/ogg;codecs=opus";
  const video = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
    ? "video/webm;codecs=vp9,opus"
    : MediaRecorder.isTypeSupported("video/webm")
      ? "video/webm"
      : "video/webm";
  return { audio, video };
}

export function RecorderWindow() {
  const [phase, setPhase] = useState<Phase>("setup");
  const [setup, setSetup] = useState<SetupData>(DEFAULT_SETUP);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [tracks, setTracks] = useState<TrackController[]>([]);
  const [paused, setPaused] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [autoDownloaded, setAutoDownloaded] = useState(false);
  const [availableMics, setAvailableMics] = useState<MicDeviceInfo[]>([]);
  const [availableCadernos, setAvailableCadernos] = useState<
    { id: string; title: string; theme: string }[]
  >([]);
  const [lectureId, setLectureId] = useState<string | null>(null);
  const [transcribeResult, setTranscribeResult] = useState<{
    persona: { label: string; color: string };
    usedBYOK: boolean;
  } | null>(null);

  // Refs (não-reativos)
  const audioCtxRef = useRef<AudioContext | null>(null);
  const destRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTsRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const tracksRef = useRef<TrackController[]>([]);
  const silenceSinceRef = useRef<Record<string, number | null>>({});

  // Enumera microfones disponíveis na fase setup. Re-enumera quando o
  // sistema avisa que o conjunto de dispositivos mudou (USB plugado/BT
  // conectado durante o setup).
  useEffect(() => {
    if (phase !== "setup") return;
    let cancelled = false;
    async function refresh() {
      const mics = await enumerateMicrophones();
      if (!cancelled) setAvailableMics(mics);
    }
    void refresh();
    const onChange = () => void refresh();
    navigator.mediaDevices.addEventListener?.("devicechange", onChange);
    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener?.("devicechange", onChange);
    };
  }, [phase]);

  // Carrega cadernos do aluno (AiStudy sessions ativos) pra dropdown de
  // vinculação no setup. Só busca uma vez quando o usuário abre o popup.
  useEffect(() => {
    if (phase !== "setup") return;
    let cancelled = false;
    void (async () => {
      try {
        const { sessions } = await api.listAiStudy();
        if (cancelled) return;
        setAvailableCadernos(
          sessions
            .filter((s) => !s.archivedAt)
            .map((s) => ({ id: s.id, title: s.title, theme: s.theme }))
        );
      } catch {
        /* sem flag de Modo Estudo → 403 silencioso; dropdown fica vazio */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  // beforeunload — só ativa em recording/finalize
  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (phase === "recording" || phase === "finalize") {
        e.preventDefault();
        e.returnValue =
          "Gravação em andamento — sair ou recarregar fará você perder tudo.";
        return e.returnValue;
      }
      return undefined;
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  // Bloqueio de Ctrl+R/F5/Ctrl+W durante gravação
  useEffect(() => {
    function key(e: KeyboardEvent) {
      if (phase !== "recording" && phase !== "finalize") return;
      const blocked =
        (e.key === "F5" && !e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R")) ||
        ((e.ctrlKey || e.metaKey) && (e.key === "w" || e.key === "W"));
      if (blocked) {
        e.preventDefault();
        // toast inline
        const el = document.createElement("div");
        el.textContent = "⏺ Gravação em andamento — atalho bloqueado.";
        el.style.cssText =
          "position:fixed;top:1rem;left:50%;transform:translateX(-50%);background:#dc2626;color:#fff;padding:.5rem 1rem;border-radius:.5rem;font:600 .8rem sans-serif;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,.3)";
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 2000);
      }
    }
    window.addEventListener("keydown", key, { capture: true });
    return () => window.removeEventListener("keydown", key, { capture: true } as any);
  }, [phase]);

  // ============================================
  // Permissões + setup do graph de áudio
  // ============================================
  async function startCapture() {
    setErrorMsg(null);
    setPhase("permission");
    try {
      const ctx = new AudioContext({ sampleRate: 48000 });
      audioCtxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();
      destRef.current = dest;
      const newTracks: TrackController[] = [];

      // Itera todos os mics selecionados (USB, BT, built-in). Cada um vira
      // sua própria trilha com noise filter individual.
      for (const micId of setup.selectedMicIds) {
        const useNoiseSupp = setup.noiseSuppression[micId] ?? true;
        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: true,
          noiseSuppression: useNoiseSupp,
          autoGainControl: false // controlamos manualmente via leveler
        };
        if (micId !== "default") {
          audioConstraints.deviceId = { exact: micId };
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints
        });
        // Label: tenta usar o do device list; cai pra "Microfone" se vazio
        const label = labelForMicId(micId, availableMics) || "Microfone";
        newTracks.push(buildTrack(ctx, dest, "mic", label, stream));
      }

      let videoStreamOnly: MediaStream | null = null;
      if (setup.useSystem || setup.captureKind === "screen_and_audio") {
        // getDisplayMedia: solicita tela + áudio do sistema (suportado em
        // Chrome/Edge; Firefox/Safari podem não devolver audio track).
        // O noiseSuppression NÃO se aplica aqui — getDisplayMedia não aceita.
        const display = await navigator.mediaDevices.getDisplayMedia({
          video: setup.captureKind === "screen_and_audio",
          audio: setup.useSystem
        });
        if (setup.useSystem) {
          const audioTracks = display.getAudioTracks();
          if (audioTracks.length > 0) {
            const audioOnly = new MediaStream(audioTracks);
            newTracks.push(
              buildTrack(ctx, dest, "system", "Áudio do sistema", audioOnly)
            );
          } else {
            console.warn(
              "Sem áudio do sistema — Firefox/Safari não retornam display audio."
            );
          }
        }
        if (setup.captureKind === "screen_and_audio") {
          videoStreamOnly = new MediaStream(display.getVideoTracks());
          videoStreamRef.current = videoStreamOnly;
        }
      }

      if (newTracks.length === 0) {
        throw new Error(
          "Nenhuma fonte de áudio selecionada. Marque pelo menos microfone ou áudio do sistema."
        );
      }

      tracksRef.current = newTracks;
      setTracks(newTracks);

      // Stream combinado pra MediaRecorder
      const combinedTracks: MediaStreamTrack[] = [];
      combinedTracks.push(...dest.stream.getAudioTracks());
      if (videoStreamOnly) combinedTracks.push(...videoStreamOnly.getVideoTracks());
      const combined = new MediaStream(combinedTracks);

      const mimes = pickMimeType();
      const mimeType =
        videoStreamOnly && combinedTracks.some((t) => t.kind === "video")
          ? mimes.video
          : mimes.audio;
      const rec = new MediaRecorder(combined, {
        mimeType,
        audioBitsPerSecond: 64000,
        videoBitsPerSecond: 1200000
      });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setResultBlob(blob);
        // Auto-download imediato — backup local. Se o upload pra
        // transcrição falhar, o usuário ainda tem o áudio cru pra retomar
        // pelo botão "Retomar" na lista.
        try {
          autoDownloadBlob(blob, setup.title, mimeType);
          setAutoDownloaded(true);
        } catch {
          /* alguns browsers bloqueiam download sem user gesture; usuário
             pode clicar no "Baixar áudio" manualmente na fase finalize */
        }
      };
      recorderRef.current = rec;
      rec.start(2000); // flush a cada 2s — chunks vão pra chunksRef
      startTsRef.current = performance.now();
      setPhase("recording");

      // Loop do leveler + VU
      const loop = () => {
        if (recorderRef.current?.state !== "recording" && recorderRef.current?.state !== "paused") {
          return;
        }
        updateLeveler();
        const sec = Math.floor((performance.now() - startTsRef.current) / 1000);
        setElapsedSec(sec);
        tickRef.current = window.setTimeout(loop, 80);
      };
      loop();
    } catch (e) {
      // Mensagem amigável priorizando dicas acionáveis. Muitos erros nativos
      // (NotAllowedError de getUserMedia/getDisplayMedia, NotFoundError quando
      // mic foi desconectado, NotReadableError em uso por outro app) vêm com
      // .message vazio no Chrome — sem fallback o aluno só via "Algo deu errado"
      // em branco. [[bug-2026-05-28: recorder error sem mensagem]]
      // Mantém console.error pra Dev Tools.
      console.error("[recorder] startCapture failed:", e);
      const name = (e as { name?: string })?.name ?? "";
      const rawMsg = e instanceof Error ? e.message?.trim() : "";
      let friendly = "";
      switch (name) {
        case "NotAllowedError":
          friendly =
            "Permissão negada. Aceite o acesso ao microfone (e à tela, se for capturar áudio do sistema) na janela do navegador.";
          break;
        case "NotFoundError":
          friendly =
            "Nenhum microfone disponível. Conecte um fone/mic e reabra o gravador.";
          break;
        case "NotReadableError":
          friendly =
            "O microfone está em uso por outro aplicativo. Feche Zoom/Meet/Teams ou outro gravador e tente de novo.";
          break;
        case "OverconstrainedError":
          friendly =
            "O microfone escolhido não suporta as opções pedidas. Tente o 'Microfone padrão do sistema'.";
          break;
        case "SecurityError":
          friendly =
            "Bloqueado pelo navegador (página não-HTTPS ou política de permissões). Tente recarregar.";
          break;
        case "AbortError":
          friendly = "Operação cancelada. Tente de novo.";
          break;
        default:
          friendly = rawMsg || `Falha ao iniciar gravação${name ? ` (${name})` : ""}.`;
      }
      const detail = rawMsg && rawMsg !== friendly ? `\n\nDetalhe técnico: ${rawMsg}` : "";
      setErrorMsg(friendly + detail);
      setPhase("error");
    }
  }

  function buildTrack(
    ctx: AudioContext,
    dest: MediaStreamAudioDestinationNode,
    source: "mic" | "system",
    label: string,
    stream: MediaStream
  ): TrackController {
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -3;
    compressor.knee.value = 6;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.005;
    compressor.release.value = 0.1;
    src.connect(analyser);
    analyser.connect(gain);
    gain.connect(compressor);
    compressor.connect(dest);
    return {
      id: `${source}-${Math.random().toString(36).slice(2, 8)}`,
      label,
      source,
      stream,
      analyser,
      gain,
      muted: false,
      currentRms: 0,
      autoGainDb: 0
    };
  }

  function updateLeveler() {
    const arr = tracksRef.current;
    if (!arr.length) return;
    const buf = new Float32Array(arr[0].analyser.fftSize);
    const next = arr.map((t) => {
      t.analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      const db = rmsToDb(rms);

      // Silence detection
      const now = performance.now();
      if (db < SILENCE_THRESHOLD_DB) {
        if (silenceSinceRef.current[t.id] == null) {
          silenceSinceRef.current[t.id] = now;
        }
      } else {
        silenceSinceRef.current[t.id] = null;
      }
      const silent =
        silenceSinceRef.current[t.id] != null &&
        now - (silenceSinceRef.current[t.id] as number) > SILENCE_MS;

      // Auto gain target
      let newGainDb = t.autoGainDb;
      if (!t.muted && !silent && db > -Infinity) {
        const diff = TARGET_RMS_DB - db;
        // Slew limit
        const step = Math.max(-GAIN_STEP_DB, Math.min(GAIN_STEP_DB, diff * 0.1));
        newGainDb = Math.max(MIN_GAIN_DB, Math.min(MAX_GAIN_DB, t.autoGainDb + step));
      } else if (silent) {
        // Abaixa devagar pra não captar chiado
        newGainDb = Math.max(MIN_GAIN_DB, t.autoGainDb - 0.2);
      }
      t.autoGainDb = newGainDb;
      const finalGain = t.muted ? 0 : dbToGain(newGainDb);
      t.gain.gain.setTargetAtTime(finalGain, t.analyser.context.currentTime, 0.02);
      t.currentRms = rms;
      return { ...t };
    });
    tracksRef.current = next as TrackController[];
    // Trigger re-render só de tempo em tempo? Por simplicidade re-render sempre.
    setTracks([...next] as TrackController[]);
  }

  function toggleMute(id: string) {
    const next = tracksRef.current.map((t) =>
      t.id === id ? { ...t, muted: !t.muted } : t
    );
    tracksRef.current = next as TrackController[];
    setTracks([...next] as TrackController[]);
  }

  function pauseToggle() {
    const rec = recorderRef.current;
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      setPaused(true);
    } else if (rec.state === "paused") {
      rec.resume();
      setPaused(false);
    }
  }

  function stop() {
    const rec = recorderRef.current;
    if (rec && (rec.state === "recording" || rec.state === "paused")) {
      rec.stop();
    }
    if (tickRef.current) {
      clearTimeout(tickRef.current);
      tickRef.current = null;
    }
    // Encerra streams
    tracksRef.current.forEach((t) =>
      t.stream.getTracks().forEach((tr) => tr.stop())
    );
    videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    setPhase("finalize");
  }

  function downloadLocal() {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement("a");
    a.href = url;
    const ext = resultBlob.type.includes("video") ? "webm" : "webm";
    a.download = `${setup.title || "gravacao"}-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function uploadAndTranscribe() {
    if (!resultBlob) return;
    setUploadProgress(1);
    try {
      // 1) Cria lecture row
      const createInput: CreateLectureInput = {
        title: setup.title.trim() || `Gravação ${new Date().toLocaleDateString("pt-BR")}`,
        kind: setup.kind,
        theme: setup.theme || undefined,
        level: setup.level || undefined,
        language: setup.language,
        summaryFocus: setup.summaryFocus,
        expectedSpeakers: setup.expectedSpeakers || undefined,
        mainSpeakerName: setup.mainSpeakerName || undefined,
        userNotes: setup.userNotes || undefined,
        captureKind: setup.captureKind,
        audioSources: [
          ...(setup.selectedMicIds.length > 0 ? ["microphone"] : []),
          ...(setup.useSystem ? ["system"] : [])
        ],
        durationSeconds: elapsedSec,
        sizeBytes: resultBlob.size,
        linkedAiStudySessionId: setup.linkedAiStudySessionId
      };
      const { lecture } = await api.createLecture(createInput);
      setLectureId(lecture.id);
      setUploadProgress(10);

      // 2) Transcribe (multipart). Sem progresso fino, mas marca pelo menos
      //    "estou enviando".
      setUploadProgress(40);
      const result = await api.transcribeLecture(lecture.id, resultBlob, elapsedSec);
      setUploadProgress(100);
      setTranscribeResult({
        persona: { label: result.persona.label, color: result.persona.color },
        usedBYOK: result.usedBYOK
      });
      setPhase("done");
    } catch (e) {
      setUploadProgress(0);
      if (e instanceof ApiError && e.status === 402) {
        setErrorMsg(
          `${e.message}\n\nDica: configure sua chave Gemini própria em "Assistente IA" pra não consumir cota da plataforma.`
        );
      } else {
        setErrorMsg(e instanceof Error ? e.message : String(e));
      }
      setPhase("error");
    }
  }

  function fmtElapsed(s: number): string {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    return `${m}:${String(sec).padStart(2, "0")}`;
  }

  // ============================================
  // Renderização por fase
  // ============================================
  return (
    <div class="flex min-h-screen flex-col bg-background text-foreground">
      {/* HEADER fixo */}
      <header class="flex items-center justify-between gap-3 border-b border-border bg-card px-5 py-3">
        <h1 class="flex items-center gap-2 text-base font-bold">
          <Mic className="size-5 text-violet-600 dark:text-violet-400" />
          Gravador de Aulas
        </h1>
        {phase === "recording" && (
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center gap-1.5 text-sm font-bold text-destructive">
              <Circle className="size-3 animate-pulse fill-current" />
              REC {fmtElapsed(elapsedSec)}
            </span>
          </div>
        )}
        {(phase === "setup" || phase === "permission" || phase === "error") && (
          <button
            type="button"
            onClick={() => window.close()}
            title="Fechar"
            aria-label="Fechar janela do gravador"
            class="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </header>

      <main class="flex-1 overflow-y-auto px-5 py-5">
        {phase === "setup" && (
          <SetupForm
            setup={setup}
            onChange={setSetup}
            onStart={startCapture}
            availableMics={availableMics}
            availableCadernos={availableCadernos}
          />
        )}

        {phase === "permission" && (
          <div class="grid place-items-center py-12 text-center">
            <Loader2 className="mx-auto size-10 animate-spin text-violet-600" />
            <p class="mt-4 text-sm">
              Aguardando permissão do navegador para microfone/tela…
            </p>
            <p class="mt-1 text-[11px] text-muted-foreground">
              Aceite os prompts pra iniciar a gravação.
            </p>
          </div>
        )}

        {phase === "recording" && (
          <RecordingView
            tracks={tracks}
            paused={paused}
            onMuteToggle={toggleMute}
            onPause={pauseToggle}
            onStop={stop}
          />
        )}

        {phase === "finalize" && (
          <FinalizeView
            blob={resultBlob}
            elapsedSec={elapsedSec}
            uploadProgress={uploadProgress}
            autoDownloaded={autoDownloaded}
            onDownload={downloadLocal}
            onUpload={uploadAndTranscribe}
            onSkip={() => window.close()}
          />
        )}

        {phase === "done" && (
          <DoneView
            transcribeResult={transcribeResult}
            lectureId={lectureId}
            onClose={() => window.close()}
            onDownload={downloadLocal}
          />
        )}

        {phase === "error" && (
          <div class="mx-auto max-w-md text-center">
            <AlertTriangle className="mx-auto size-10 text-destructive" />
            <h2 class="mt-3 text-lg font-bold">Algo deu errado</h2>
            <p class="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              {errorMsg}
            </p>
            <div class="mt-4 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setPhase("setup")}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => window.close()}
                class="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-white hover:brightness-110"
              >
                Fechar janela
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================
// Sub-componentes
// ============================================
function SetupForm({
  setup,
  onChange,
  onStart,
  availableMics,
  availableCadernos
}: {
  setup: SetupData;
  onChange: (s: SetupData) => void;
  onStart: () => void;
  availableMics: MicDeviceInfo[];
  availableCadernos: { id: string; title: string; theme: string }[];
}) {
  const hasAnyMic = setup.selectedMicIds.length > 0;
  const valid =
    setup.title.trim().length > 0 && (hasAnyMic || setup.useSystem);

  function toggleMic(id: string) {
    const cur = setup.selectedMicIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange({ ...setup, selectedMicIds: next });
  }

  function toggleNoise(key: string) {
    onChange({
      ...setup,
      noiseSuppression: {
        ...setup.noiseSuppression,
        [key]: !(setup.noiseSuppression[key] ?? true)
      }
    });
  }

  function patch(p: Partial<SetupData>) {
    onChange({ ...setup, ...p });
  }

  return (
    <div class="mx-auto max-w-2xl space-y-4">
      <header class="space-y-1">
        <h2 class="text-lg font-bold">Configurar gravação</h2>
        <p class="text-sm text-muted-foreground">
          Estes detalhes ajudam a IA a transcrever e resumir com mais precisão.
          Tudo pode ser editado depois.
        </p>
      </header>

      <Field label="Título *" required>
        <input
          type="text"
          value={setup.title}
          onInput={(e) => patch({ title: (e.target as HTMLInputElement).value })}
          placeholder="Ex: Aula 3 — Cardiopatias congênitas"
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </Field>

      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Tipo">
          <select
            value={setup.kind}
            onChange={(e) =>
              patch({ kind: (e.target as HTMLSelectElement).value as LectureKind })
            }
            aria-label="Tipo da sessão"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="aula">Aula</option>
            <option value="palestra">Palestra</option>
            <option value="reuniao">Reunião</option>
            <option value="entrevista">Entrevista</option>
            <option value="estudo">Estudo sozinho</option>
            <option value="outro">Outro</option>
          </select>
        </Field>
        <Field label="Foco do resumo">
          <select
            value={setup.summaryFocus}
            onChange={(e) =>
              patch({
                summaryFocus: (e.target as HTMLSelectElement)
                  .value as LectureFocus
              })
            }
            aria-label="Foco do resumo"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="detailed_notes">Anotações detalhadas</option>
            <option value="executive">Resumo executivo</option>
            <option value="glossary">Glossário de termos</option>
            <option value="exam_questions">Questões de revisão</option>
          </select>
        </Field>
      </div>

      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Tema / disciplina">
          <input
            type="text"
            value={setup.theme}
            onInput={(e) => patch({ theme: (e.target as HTMLInputElement).value })}
            placeholder="Ex: Cardiologia, Direito Tributário"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Nível">
          <input
            type="text"
            value={setup.level}
            onInput={(e) => patch({ level: (e.target as HTMLInputElement).value })}
            placeholder="Ex: graduação, pós, livre"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <div class="grid gap-3 md:grid-cols-2">
        <Field label="Orador principal (opcional)">
          <input
            type="text"
            value={setup.mainSpeakerName}
            onInput={(e) =>
              patch({ mainSpeakerName: (e.target as HTMLInputElement).value })
            }
            placeholder="Ex: Prof. Maria Santos"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Falantes esperados">
          <input
            type="number"
            min={1}
            max={20}
            title="Número aproximado de falantes"
            aria-label="Número de falantes esperados"
            value={setup.expectedSpeakers}
            onInput={(e) =>
              patch({
                expectedSpeakers: parseInt((e.target as HTMLInputElement).value, 10) || 1
              })
            }
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm tabular-nums"
          />
        </Field>
      </div>

      <Field label="Instruções extras pra IA (opcional)">
        <textarea
          value={setup.userNotes}
          onInput={(e) =>
            patch({ userNotes: (e.target as HTMLTextAreaElement).value })
          }
          rows={2}
          placeholder='Ex: "Ignore a parte do final que foi off-topic" / "Foque na fórmula do índice de Apgar"'
          class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </Field>

      {availableCadernos.length > 0 && (
        <Field label="Vincular a caderno do Modo Estudo (opcional)">
          <select
            value={setup.linkedAiStudySessionId ?? ""}
            onChange={(e) =>
              patch({
                linkedAiStudySessionId:
                  (e.target as HTMLSelectElement).value || null
              })
            }
            title="Vincular esta gravação a um caderno do Modo Estudo"
            aria-label="Vincular a caderno do Modo Estudo"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="">— não vincular —</option>
            {availableCadernos.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} {c.theme ? `· ${c.theme}` : ""}
              </option>
            ))}
          </select>
          <p class="mt-1 text-[11px] text-muted-foreground">
            Ao vincular, o resumo e a transcrição entram como documentos do
            caderno automaticamente quando a IA terminar de processar.
          </p>
        </Field>
      )}

      <fieldset class="space-y-3 rounded-md border border-border bg-card p-4">
        <legend class="px-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Fontes de áudio
        </legend>

        <div class="space-y-1">
          <div class="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Microfones detectados</span>
            <span class="text-[10px] normal-case opacity-70">
              {availableMics.length === 0
                ? "(permita acesso pra listar)"
                : `${availableMics.length} ativos`}
            </span>
          </div>
          {availableMics.length === 0 && (
            <label class="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
              <Checkbox
                checked={setup.selectedMicIds.includes("default")}
                onCheckedChange={() => toggleMic("default")}
              />
              <span class="flex-1">Microfone padrão do sistema</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  toggleNoise("default");
                }}
                title="Liga/desliga supressão de ruído nessa fonte"
                class={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                  setup.noiseSuppression.default ?? true
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {setup.noiseSuppression.default ?? true
                  ? "🛡️ Filtro ON"
                  : "Filtro OFF"}
              </button>
            </label>
          )}
          {availableMics.map((mic) => {
            const checked = setup.selectedMicIds.includes(mic.deviceId);
            const noiseOn = setup.noiseSuppression[mic.deviceId] ?? true;
            return (
              <label
                key={mic.deviceId}
                class="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleMic(mic.deviceId)}
                />
                <span class="min-w-0 flex-1 truncate" title={mic.label}>
                  {mic.label}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleNoise(mic.deviceId);
                  }}
                  title="Liga/desliga supressão de ruído nessa fonte"
                  class={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                    noiseOn
                      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {noiseOn ? "🛡️ Filtro ON" : "Filtro OFF"}
                </button>
              </label>
            );
          })}
          <p class="text-[11px] text-muted-foreground">
            Auto-leveler ajusta volume e corta silêncio em cada microfone
            individualmente. Filtro = noise suppression do browser por fonte.
          </p>
        </div>

        <label class="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
          <Checkbox
            checked={setup.useSystem}
            onCheckedChange={(v) => patch({ useSystem: v === true })}
          />
          <span class="flex-1">
            <span class="font-medium">Áudio do sistema</span>
            <span class="block text-xs text-muted-foreground">
              Som do seu computador (Zoom/Meet/YouTube). Limitado em
              Firefox/Safari. Não aceita filtro (browser não expõe).
            </span>
          </span>
        </label>
        <label class="mt-2 flex cursor-pointer items-start gap-2 text-sm">
          <Checkbox
            checked={setup.captureKind === "screen_and_audio"}
            onCheckedChange={(v) => {
              const screen = v === true;
              patch({
                captureKind: screen ? "screen_and_audio" : "audio_only",
                // Quando ligar captura de tela, já marca áudio do sistema
                // automaticamente — é o caso de uso típico (aula online).
                useSystem: screen ? true : setup.useSystem
              });
            }}
            className="mt-0.5"
          />
          <span>
            <span class="font-medium">Capturar vídeo da tela também</span>
            <span class="block text-xs text-muted-foreground">
              Útil pra aulas com slides/quadro. O arquivo final fica bem maior.
              IA usa só o áudio.
            </span>
          </span>
        </label>
      </fieldset>

      {(setup.useSystem || setup.captureKind === "screen_and_audio") && (
        <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
          <Volume2 className="mr-1 inline size-3.5" />
          <strong>Importante pra áudio do sistema:</strong> quando o Chrome
          abrir a janela de seleção de tela/janela, certifique-se de marcar a
          opção <strong>"Compartilhar áudio"</strong> (canto inferior do
          diálogo). Sem isso, o som do computador não é capturado.
        </div>
      )}

      <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        <AlertTriangle className="mr-1 inline size-3.5" />
        <strong>Aviso:</strong> esta janela não pode ser recarregada nem
        fechada durante a gravação. O áudio fica salvo no seu dispositivo —
        nada vai pra cloud até você confirmar.
      </div>

      <div class="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => window.close()}
          class="rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onStart}
          disabled={!valid}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          <Play className="size-4" />
          Iniciar gravação
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children
}: {
  label: string;
  required?: boolean;
  children: any;
}) {
  return (
    <label class="block space-y-1">
      <span class="block text-xs font-medium text-muted-foreground">
        {label}
        {required && <span class="ml-1 text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}

function RecordingView({
  tracks,
  paused,
  onMuteToggle,
  onPause,
  onStop
}: {
  tracks: TrackController[];
  paused: boolean;
  onMuteToggle: (id: string) => void;
  onPause: () => void;
  onStop: () => void;
}) {
  return (
    <div class="mx-auto max-w-3xl space-y-5">
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-center">
        <p class="text-sm font-bold text-destructive">
          {paused ? "⏸ PAUSADA" : "🔴 GRAVANDO"}
        </p>
        <p class="mt-1 text-xs text-muted-foreground">
          Não recarregue nem feche essa janela. Atalhos de refresh estão
          bloqueados.
        </p>
      </div>

      <div class="space-y-3">
        {tracks.map((t) => (
          <TrackRow key={t.id} track={t} onMuteToggle={onMuteToggle} />
        ))}
      </div>

      <div class="flex items-center justify-center gap-3 pt-4">
        <button
          type="button"
          onClick={onPause}
          class="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
        >
          {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
          {paused ? "Continuar" : "Pausar"}
        </button>
        <button
          type="button"
          onClick={onStop}
          class="inline-flex items-center gap-2 rounded-md bg-destructive px-5 py-2 text-sm font-semibold text-white hover:brightness-110"
        >
          <Square className="size-4" />
          Encerrar
        </button>
      </div>
    </div>
  );
}

function TrackRow({
  track,
  onMuteToggle
}: {
  track: TrackController;
  onMuteToggle: (id: string) => void;
}) {
  const db = rmsToDb(track.currentRms);
  // Mapeia dB [-60, 0] em 0..100% pro VU
  const pct = Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
  return (
    <div class="rounded-md border border-border bg-card p-3">
      <div class="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onMuteToggle(track.id)}
          title={track.muted ? "Tirar mudo" : "Silenciar"}
          aria-label={track.muted ? "Tirar mudo" : "Silenciar"}
          class={`grid size-9 shrink-0 place-items-center rounded-md ${
            track.muted
              ? "bg-destructive/10 text-destructive"
              : "bg-emerald-500/10 text-emerald-600"
          }`}
        >
          {track.muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
        </button>
        <div class="min-w-0 flex-1">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-sm font-semibold">{track.label}</span>
            <span class="font-mono text-[10px] text-muted-foreground">
              auto: {track.autoGainDb >= 0 ? "+" : ""}
              {track.autoGainDb.toFixed(1)} dB · {db === -Infinity ? "—∞" : db.toFixed(1)} dBFS
            </span>
          </div>
          <div class="mt-1 h-2 overflow-hidden rounded-full bg-muted">
            <div
              class={`h-full transition-[width] duration-75 ${
                pct > 90
                  ? "bg-destructive"
                  : pct > 70
                    ? "bg-amber-500"
                    : "bg-emerald-500"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FinalizeView({
  blob,
  elapsedSec,
  uploadProgress,
  autoDownloaded,
  onDownload,
  onUpload,
  onSkip
}: {
  blob: Blob | null;
  elapsedSec: number;
  uploadProgress: number;
  autoDownloaded: boolean;
  onDownload: () => void;
  onUpload: () => void;
  onSkip: () => void;
}) {
  return (
    <div class="mx-auto max-w-xl space-y-5 text-center">
      <CheckCircle2 className="mx-auto size-12 text-emerald-600" />
      <div>
        <h2 class="text-lg font-bold">Gravação concluída</h2>
        <p class="text-sm text-muted-foreground">
          {elapsedSec}s · {blob ? `${(blob.size / 1024 / 1024).toFixed(1)} MB` : "—"}
        </p>
      </div>

      {autoDownloaded && (
        <div class="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-left text-xs text-emerald-700 dark:text-emerald-300">
          <strong>Backup salvo na pasta Downloads.</strong> Se o envio para
          transcrição falhar, dá pra retomar pelo botão "Retomar transcrição"
          na lista de gravações, escolhendo este arquivo.
        </div>
      )}

      {uploadProgress > 0 && uploadProgress < 100 && (
        <div class="space-y-1">
          <div class="h-2 overflow-hidden rounded-full bg-muted">
            <div
              class="h-full bg-violet-500 transition-[width]"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p class="text-xs text-muted-foreground">
            {uploadProgress < 40 ? "Criando registro…" : "Enviando áudio pra IA…"}
          </p>
        </div>
      )}

      <div class="flex flex-col gap-2">
        <button
          type="button"
          onClick={onDownload}
          class="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-accent"
        >
          <Download className="size-4" />
          Baixar arquivo local
        </button>
        <button
          type="button"
          onClick={onUpload}
          disabled={uploadProgress > 0 && uploadProgress < 100}
          class="inline-flex items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {uploadProgress > 0 && uploadProgress < 100 ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4" />
          )}
          Gerar transcrição + resumo
        </button>
        <button
          type="button"
          onClick={onSkip}
          class="text-xs text-muted-foreground hover:underline"
        >
          Fechar sem transcrever (mantenho só o arquivo local)
        </button>
      </div>
    </div>
  );
}

function DoneView({
  transcribeResult,
  lectureId,
  onClose,
  onDownload
}: {
  transcribeResult: {
    persona: { label: string; color: string };
    usedBYOK: boolean;
  } | null;
  lectureId: string | null;
  onClose: () => void;
  onDownload: () => void;
}) {
  return (
    <div class="mx-auto max-w-xl space-y-5 text-center">
      <CheckCircle2 className="mx-auto size-14 text-emerald-600" />
      <div>
        <h2 class="text-xl font-bold">Pronto!</h2>
        <p class="mt-1 text-sm text-muted-foreground">
          Transcrição e resumo gerados.{" "}
          {transcribeResult?.usedBYOK
            ? "Sua chave Gemini foi usada (sem cota da plataforma)."
            : "Cota da plataforma consumida."}
        </p>
        {transcribeResult?.persona.label && (
          <div class="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-3 py-1 text-xs text-violet-700 dark:text-violet-300">
            Resumo gerado por: <strong>{transcribeResult.persona.label}</strong>
          </div>
        )}
      </div>

      <div class="flex flex-col items-stretch gap-2">
        <button
          type="button"
          onClick={onDownload}
          class="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm hover:bg-accent"
        >
          <Download className="size-4" />
          Baixar arquivo local
        </button>
        <button
          type="button"
          onClick={() => {
            // Foca o opener e fecha esta janela
            if (window.opener) {
              try {
                window.opener.focus();
              } catch {
                /* noop */
              }
            }
            onClose();
          }}
          class="inline-flex items-center justify-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
        >
          Fechar janela e ver no app
        </button>
      </div>
    </div>
  );
}

